import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Divider,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Receipt,
  Euro,
  FileDownload,
} from '@mui/icons-material';
import { externalDocumentsService } from '../services/api';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const COLORS = ['#1F2937', '#4B5563', '#A7AFB9', '#CBD5E1', '#94A3B8', '#64748B'];

const AccountingReports = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [documents, setDocuments] = useState([]);
  const [, setStats] = useState(null);
  const [, setLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, [year, month]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [docs, statsData] = await Promise.all([
        externalDocumentsService.getAll(),
        externalDocumentsService.getStats(),
      ]);
      setDocuments(docs);
      setStats(statsData);
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  // Calculs des rapports
  const getMonthlyData = () => {
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
    const data = months.map((month, index) => {
      const monthDocs = documents.filter(doc => {
        const date = new Date(doc.documentDate);
        return date.getFullYear() === year && date.getMonth() === index;
      });

      const totalHT = monthDocs.reduce((sum, doc) => sum + doc.amount, 0);
      const totalTVA = monthDocs.reduce((sum, doc) => sum + doc.vatAmount, 0);
      const totalTTC = monthDocs.reduce((sum, doc) => sum + doc.totalTTC, 0);

      return {
        month,
        'Montant HT': totalHT,
        'TVA': totalTVA,
        'Total TTC': totalTTC,
      };
    });

    return data;
  };

  const getCategoryData = () => {
    const categoryMap = {};
    
    documents.forEach(doc => {
      const docDate = new Date(doc.documentDate);
      if (docDate.getFullYear() === year) {
        if (!categoryMap[doc.category]) {
          categoryMap[doc.category] = 0;
        }
        categoryMap[doc.category] += doc.totalTTC;
      }
    });

    return Object.entries(categoryMap).map(([name, value]) => ({
      name,
      value,
    }));
  };

  const getVATReport = () => {
    const vatMap = {};
    
    documents.forEach(doc => {
      const docDate = new Date(doc.documentDate);
      if (docDate.getFullYear() === year && docDate.getMonth() + 1 === month) {
        const rate = doc.vatRate.toString();
        if (!vatMap[rate]) {
          vatMap[rate] = {
            baseHT: 0,
            vatAmount: 0,
            totalTTC: 0,
            count: 0,
          };
        }
        vatMap[rate].baseHT += doc.amount;
        vatMap[rate].vatAmount += doc.vatAmount;
        vatMap[rate].totalTTC += doc.totalTTC;
        vatMap[rate].count += 1;
      }
    });

    return Object.entries(vatMap).map(([rate, data]) => ({
      rate,
      ...data,
    }));
  };

  const getAnnualTotal = () => {
    const yearDocs = documents.filter(doc => {
      const date = new Date(doc.documentDate);
      return date.getFullYear() === year;
    });

    return {
      totalHT: yearDocs.reduce((sum, doc) => sum + doc.amount, 0),
      totalTVA: yearDocs.reduce((sum, doc) => sum + doc.vatAmount, 0),
      totalTTC: yearDocs.reduce((sum, doc) => sum + doc.totalTTC, 0),
      count: yearDocs.length,
    };
  };

  const exportAnnualReport = () => {
    const monthlyData = getMonthlyData();
    const categoryData = getCategoryData();
    const annualTotal = getAnnualTotal();

    const headers = ['Mois', 'Montant HT', 'TVA', 'Total TTC'];
    const rows = monthlyData.map(m => [
      m.month,
      m['Montant HT'].toFixed(2),
      m['TVA'].toFixed(2),
      m['Total TTC'].toFixed(2),
    ]);

    rows.push(['', '', '', '']);
    rows.push(['TOTAL ANNÉE', annualTotal.totalHT.toFixed(2), annualTotal.totalTVA.toFixed(2), annualTotal.totalTTC.toFixed(2)]);
    rows.push(['', '', '', '']);
    rows.push(['RÉPARTITION PAR CATÉGORIE', '', '', '']);
    
    categoryData.forEach(cat => {
      rows.push([cat.name, '', '', cat.value.toFixed(2)]);
    });

    const csvContent = [
      `Rapport Comptable Annuel ${year}`,
      '',
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rapport-comptable-${year}.csv`;
    link.click();
  };

  const monthlyData = getMonthlyData();
  const categoryData = getCategoryData();
  const vatReport = getVATReport();
  const annualTotal = getAnnualTotal();

  return (
    <Box>
      {/* En-tête */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Rapports Comptables
        </Typography>
        <Button
          variant="contained"
          startIcon={<FileDownload />}
          onClick={exportAnnualReport}
        >
          Export Rapport Annuel
        </Button>
      </Box>

      {/* Sélecteurs */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Année</InputLabel>
                <Select value={year} onChange={(e) => setYear(e.target.value)} label="Année">
                  {[2024, 2025, 2026, 2027].map((y) => (
                    <MenuItem key={y} value={y}>{y}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel>Mois (pour TVA)</InputLabel>
                <Select value={month} onChange={(e) => setMonth(e.target.value)} label="Mois (pour TVA)">
                  {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'].map((m, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Totaux annuels */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Receipt color="primary" sx={{ mr: 1 }} />
                <Typography color="text.secondary">Documents</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {annualTotal.count}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <Euro color="info" sx={{ mr: 1 }} />
                <Typography color="text.secondary">Total HT</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatAmount(annualTotal.totalHT)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUp color="warning" sx={{ mr: 1 }} />
                <Typography color="text.secondary">TVA</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatAmount(annualTotal.totalTVA)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingDown color="error" sx={{ mr: 1 }} />
                <Typography color="text.secondary">Total TTC</Typography>
              </Box>
              <Typography variant="h4" fontWeight="bold">
                {formatAmount(annualTotal.totalTTC)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Graphique mensuel */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Évolution Mensuelle {year}
          </Typography>
          <Box sx={{ width: '100%', height: 400 }}>
            <ResponsiveContainer>
              <BarChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatAmount(value)} />
                <Legend />
                <Bar dataKey="Montant HT" fill="#1F2937" />
                <Bar dataKey="TVA" fill="#A7AFB9" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Répartition par catégorie */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Répartition par Catégorie
              </Typography>
              <Box sx={{ width: '100%', height: 400 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.name}: ${formatAmount(entry.value)}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatAmount(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Déclaration TVA */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Déclaration TVA - {['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'][month - 1]} {year}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Taux TVA</TableCell>
                      <TableCell align="right">Base HT</TableCell>
                      <TableCell align="right">Montant TVA</TableCell>
                      <TableCell align="center">Nb</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vatReport.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Aucune donnée pour ce mois
                        </TableCell>
                      </TableRow>
                    ) : (
                      <>
                        {vatReport.map((row) => (
                          <TableRow key={row.rate}>
                            <TableCell>{row.rate}%</TableCell>
                            <TableCell align="right">{formatAmount(row.baseHT)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                              {formatAmount(row.vatAmount)}
                            </TableCell>
                            <TableCell align="center">{row.count}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={4}>
                            <Divider />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                            {formatAmount(vatReport.reduce((sum, r) => sum + r.baseHT, 0))}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatAmount(vatReport.reduce((sum, r) => sum + r.vatAmount, 0))}
                          </TableCell>
                          <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                            {vatReport.reduce((sum, r) => sum + r.count, 0)}
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountingReports;
